import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pickVoice, useVoices } from '../../hooks/useSpeech';

/**
 * Speaking, in more than one language, one utterance at a time.
 *
 * `useSpeech` is built for a card's audio button: one language, fire and
 * forget. A hands-free drill needs neither of those. It runs a sequence —
 * ask, listen, answer — so each step has to be awaited, and two of the three
 * modes speak English to you and listen in Spanish, so the language changes
 * mid-card.
 *
 * Both hooks share `pickVoice` and one voice list, so the rule about never
 * speaking a language in the wrong voice holds here too: `voiceFor` returning
 * null means the mode cannot run, and the panel says so rather than reading
 * Spanish aloud in an American accent.
 */

export interface Speaker {
  supported: boolean;
  /** Resolves when the utterance finishes, errors, or is cancelled. */
  speak: (text: string, lang: string, rate?: number) => Promise<void>;
  cancel: () => void;
  speaking: boolean;
  /** The voice that would be used for a tag, or null if there is none. */
  voiceFor: (lang: string) => SpeechSynthesisVoice | null;
  /** Every voice the browser offers, for the diagnostics panel. */
  voices: SpeechSynthesisVoice[];
}

export function useSpeaker(): Speaker {
  const voices = useVoices();
  const [speaking, setSpeaking] = useState(false);
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Resolver for the utterance in flight, so cancelling can settle it rather
  // than leaving the drill awaiting a promise that will never complete.
  const pending = useRef<(() => void) | null>(null);

  const settle = useCallback(() => {
    const resolve = pending.current;
    pending.current = null;
    setSpeaking(false);
    resolve?.();
  }, []);

  const cancel = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    settle();
  }, [supported, settle]);

  // Leaving the drill must not leave a voice talking over the next page.
  useEffect(() => {
    if (!supported) return;
    return () => {
      window.speechSynthesis.cancel();
      pending.current?.();
      pending.current = null;
    };
  }, [supported]);

  const voiceFor = useCallback(
    (lang: string) => pickVoice(voices, lang),
    [voices],
  );

  const speak = useCallback(
    (text: string, lang: string, rate = 0.9): Promise<void> => {
      if (!supported || !text.trim()) return Promise.resolve();
      const voice = pickVoice(voices, lang);
      // Refusing is the whole point: a missing Spanish voice must not become an
      // English one reading Spanish words.
      if (!voice) return Promise.resolve();

      window.speechSynthesis.cancel();
      pending.current?.();

      return new Promise<void>((resolve) => {
        pending.current = resolve;

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.voice = voice;
        // Some engines consult lang for pronunciation rules even with a voice set.
        utterance.lang = voice.lang;
        utterance.rate = Math.min(Math.max(rate, 0.5), 1.5);
        utterance.onstart = () => setSpeaking(true);
        utterance.onend = settle;
        utterance.onerror = settle;

        window.speechSynthesis.speak(utterance);

        // Chrome drops utterances silently when the tab is backgrounded or the
        // engine wedges. A drill with no keyboard in reach cannot wait forever,
        // so a generous ceiling settles it and the sequence moves on.
        const ceilingMs = Math.max(4000, text.length * 140);
        setTimeout(() => {
          if (pending.current === resolve) settle();
        }, ceilingMs);
      });
    },
    [supported, voices, settle],
  );

  return useMemo(
    () => ({ supported, speak, cancel, speaking, voiceFor, voices }),
    [supported, speak, cancel, speaking, voiceFor, voices],
  );
}
